import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Package, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [orderId, setOrderId] = useState(null);
  const [track, setTrack] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderId(data.order_id);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { cleared.current = true; clear(); }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
        polls += 1;
        if (polls >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        // Transient network error — retry a few times before giving up.
        polls += 1;
        if (polls >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (state !== "paid" || !orderId) return undefined;
    let dead = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/tracking`);
        if (!dead) setTrack(data);
      } catch (e) { /* tracking is best-effort */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { dead = true; clearInterval(t); };
  }, [state, orderId]);

  return (
    <main className="pt-28 md:pt-36 pb-32 px-4">
      <div className="max-w-[640px] mx-auto text-center rounded-2xl border border-white/10 bg-card p-10">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-neon animate-spin" />
            <h1 className="font-display text-4xl text-white mt-6">Confirming payment…</h1>
            <p className="mt-2 text-white/60">Hang tight, this only takes a second.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-neon" />
            <h1 className="font-display text-4xl md:text-5xl text-white mt-6">You're in. Order confirmed.</h1>
            {orderId && <p className="mt-3 text-white/60">Order <span className="text-neon font-bold">{orderId}</span></p>}
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-white/60">
              <Package size={16} className="text-neon" /> Made-to-order · a confirmation email is on its way.
            </div>
            {track && (
              <div data-testid="order-tracking" className="mt-6 text-left rounded-xl border border-white/10 bg-black/30 p-5">
                <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Fulfillment</p>
                <p className="text-sm text-white/80 capitalize" data-testid="order-tracking-status">
                  {(track.printify_status || track.fulfillment_status || "processing").replace(/[-_]/g, " ")}
                </p>
                {(track.shipments || []).length > 0 ? (
                  track.shipments.map((s, i) => (
                    <div key={i} className="mt-2 text-sm text-white/70">
                      {(s.carrier || "").toUpperCase()} · {s.tracking_number || "tracking pending"}
                      {s.tracking_url && (
                        <> — <a href={s.tracking_url} target="_blank" rel="noreferrer" data-testid={`order-track-link-${i}`} className="text-neon hover:underline">Track package</a></>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="mt-1 text-xs text-white/40">Tracking appears here once your order ships.</p>
                )}
              </div>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/shop" className="bg-neon text-black font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full">Keep shopping</Link>
              <Link to="/" className="border border-white/30 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full hover:border-neon hover:text-neon transition">Home</Link>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={48} className="mx-auto text-neon animate-spin" />
            <h1 className="font-display text-4xl text-white mt-6">Payment processing</h1>
            <p className="mt-2 text-white/60">Your payment is still processing. {orderId && `Order ${orderId}.`} We'll email your confirmation shortly.</p>
            <Link to="/" className="inline-block mt-8 bg-neon text-black font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full">Back home</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={52} className="mx-auto text-trash" />
            <h1 className="font-display text-4xl text-white mt-6">We couldn't confirm this</h1>
            <p className="mt-2 text-white/60">If you were charged, your confirmation email is the source of truth. Otherwise, try again.</p>
            <Link to="/shop" className="inline-block mt-8 bg-neon text-black font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full">Back to shop</Link>
          </>
        )}
      </div>
    </main>
  );
}
